import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { alertActions } from "../redux/AlertController";
import useLogout from "./useLogout";

// Reuses the same recipients/send endpoints the notifications page uses —
// just always includes this group's id as a fixed filter alongside
// whatever else the admin picks.
export const useGroupNotifications = (groupId) => {
    const [phoneNumbers, setPhoneNumbers] = useState([]);
    const [isLoadingRecipients, setIsLoadingRecipients] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    const dispatch = useDispatch();
    const user = useSelector(state => state.userController.user);
    const navigate = useNavigate();
    const { logout } = useLogout();

    const fetchRecipients = async (filters) => {
        if (!user) {
            navigate('/login');
            return;
        }
        setIsLoadingRecipients(true);
        try {
            const params = new URLSearchParams({ group: groupId });
            Object.entries(filters).forEach(([key, value]) => {
                if (Array.isArray(value)) {
                    value.forEach((v) => params.append(key, v));
                } else if (value !== '' && value !== null && value !== undefined) {
                    params.append(key, value);
                }
            });

            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/notifications/recipients?${params}`, {
                headers: { 'Authorization': `Bearer ${user?.token}` }
            });
            const json = await res.json();

            if (res.status === 401) {
                logout();
            } else if (!res.ok) {
                dispatch(alertActions.showAlert({ msg: json.message, type: 'error' }));
            } else {
                setPhoneNumbers(json.phoneNumbers);
                setHasSearched(true);
            }
        } catch (err) {
            dispatch(alertActions.showAlert({ msg: err.message, type: 'error' }));
        }
        setIsLoadingRecipients(false);
    };

    const resetRecipients = () => {
        setPhoneNumbers([]);
        setHasSearched(false);
    };

    const sendNotification = async (message, filters) => {
        setIsSending(true);
        let success = false;
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/notifications/send`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user?.token}`
                },
                body: JSON.stringify({ phoneNumbers, message, filters })
            });
            const json = await res.json();

            if (res.status === 401) {
                logout();
            } else if (!res.ok) {
                dispatch(alertActions.showAlert({ msg: json.message, type: 'error' }));
            } else {
                dispatch(alertActions.showAlert({ msg: json.message, type: 'success' }));
                success = true;
            }
        } catch (err) {
            dispatch(alertActions.showAlert({ msg: err.message, type: 'error' }));
        }
        setIsSending(false);
        return success;
    };

    return { phoneNumbers, isLoadingRecipients, isSending, hasSearched, fetchRecipients, resetRecipients, sendNotification };
};
